import { useCallback, useEffect, useRef, useState } from 'react';
import * as locator from '@arcgis/core/rest/locator';
import type MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import { TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { dispatchTargetedEvent } from '@Map/utils';
import CustomSearchService from './CustomSearchService';
import { FlyTo, GeocoderResult, SearchEndpoint } from './types';
import type { EsriLayerSelectionApi } from '../useLayers';
import type { EsriSelectionApi } from '../../EsriMapContext';
import {
	RESTRICT_SEARCH,
	SEARCH_CHANGED,
	SEARCH_FLYTO,
	SET_DEFAULT_SEARCH,
	STYLE_SEARCH,
} from './constants';

const DROP_PIN_LAYER_ID = 'search-drop-pin';

// Teardrop pin matching Mapbox Search._searchMarker (color: '#4668f2').
const DROP_PIN_SVG = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
	'<svg xmlns="http://www.w3.org/2000/svg" width="27" height="41" viewBox="0 0 27 41">' +
		'<path fill="#4668f2" d="M13.5 0C6.044 0 0 6.044 0 13.5c0 9.188 13.5 27.5 13.5 27.5S27 22.688 27 13.5C27 6.044 20.956 0 13.5 0z"/>' +
		'<circle fill="white" cx="13.5" cy="13.5" r="5.5"/>' +
		'</svg>',
)}`;

const WORLD_GEOCODER_URL =
	'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer';

const COORD_REGEX =
	/^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i;

const SEARCH_LIMIT = 3;

export interface UseSearchLogicParams {
	searchEndpoints?: SearchEndpoint[];
	onSearch?: (result: GeocoderResult) => void;
	geocoderId?: string | null;
	view: MapView | null;
	layerSelectionApi: EsriLayerSelectionApi | null;
	getLayerIdsByServiceId?: (serviceId: string) => string[];
	selectionApi?: EsriSelectionApi | null;
	restrictSearchToBounds?: boolean;
}

export interface UseSearchLogicResult {
	onInputChange: (query: string) => Promise<GeocoderResult[]>;
	onSelect: (result: GeocoderResult | null) => void;
	defaultSearch: string;
	setDefaultSearch: (value: string) => void;
	textFieldProps: MuiTextFieldProps;
}

export const useSearchLogic = ({
	searchEndpoints,
	onSearch,
	geocoderId,
	view,
	layerSelectionApi,
	getLayerIdsByServiceId,
	selectionApi,
	restrictSearchToBounds = false,
}: UseSearchLogicParams): UseSearchLogicResult => {
	const [defaultSearch, setDefaultSearch] = useState('');
	const [textFieldProps, setTextFieldProps] = useState<MuiTextFieldProps>({});

	// Use refs for values that control behavior but shouldn't retrigger renders
	const flyToRef = useRef<FlyTo>(FlyTo.always);
	const restrictToBoundsRef = useRef(restrictSearchToBounds);
	const defaultSearchFiredRef = useRef(false);
	// Center to fly to once the view becomes available (handles the race where
	// onSelect fires before the map has finished initializing).
	const pendingFlyToRef = useRef<[number, number] | null>(null);

	// Apply any flyTo that was deferred because view wasn't ready yet
	useEffect(() => {
		if (!view || !pendingFlyToRef.current) return;
		view.goTo({ center: pendingFlyToRef.current, zoom: 16 });
		pendingFlyToRef.current = null;
	}, [view]);

	// Drop-pin graphics layer — created when view is ready, torn down on cleanup
	const dropPinLayerRef = useRef<GraphicsLayer | null>(null);
	const pendingDropPinRef = useRef<[number, number] | null>(null);

	const addPinGraphic = useCallback(
		(layer: GraphicsLayer, center: [number, number]): void => {
			layer.removeAll();
			layer.add(
				new Graphic({
					geometry: new Point({
						longitude: center[0],
						latitude: center[1],
					}),
					symbol: {
						type: 'picture-marker' as const,
						url: DROP_PIN_SVG,
						width: '27px',
						height: '41px',
						yoffset: '20.5px',
					},
				}),
			);
		},
		[],
	);

	useEffect(() => {
		const esriMap = view?.map;
		if (!esriMap) return;
		const layer = new GraphicsLayer({ id: DROP_PIN_LAYER_ID });
		esriMap.add(layer);
		dropPinLayerRef.current = layer;
		// Apply any pin that was requested before the layer existed
		if (pendingDropPinRef.current) {
			addPinGraphic(layer, pendingDropPinRef.current);
			pendingDropPinRef.current = null;
		}
		return () => {
			esriMap.remove(layer);
			dropPinLayerRef.current = null;
		};
	}, [view, addPinGraphic]);

	const setDropPin = useCallback(
		(center: [number, number] | null): void => {
			if (!center) {
				pendingDropPinRef.current = null;
				dropPinLayerRef.current?.removeAll();
				return;
			}
			const layer = dropPinLayerRef.current;
			if (!layer) {
				// Layer not ready yet — defer until view initialises
				pendingDropPinRef.current = center;
				return;
			}
			addPinGraphic(layer, center);
		},
		[addPinGraphic],
	);

	// Use a ref for onSearch to avoid stale closures in onSelect
	const onSearchRef = useRef(onSearch);
	useEffect(() => {
		onSearchRef.current = onSearch;
	}, [onSearch]);

	// Attach event listeners to the external geocoder container
	useEffect(() => {
		if (!geocoderId) return;

		const container = document.getElementById(geocoderId);
		if (!container) return;

		// Pick up any defaultSearch that EsriGeocoder already wrote before our
		// listener was ready (sibling useEffect timing race).
		const pending = (container as HTMLElement).dataset?.defaultSearch;
		if (pending) {
			defaultSearchFiredRef.current = true;
			setDefaultSearch(pending);
		}

		const onSetDefaultSearch = (e: Event) => {
			const search = (e as CustomEvent<string>).detail;
			defaultSearchFiredRef.current = true;
			setDefaultSearch(search);
		};

		const onSetFlyTo = (e: Event) => {
			const flyTo = (e as CustomEvent<FlyTo>).detail;
			if (flyTo && flyTo in FlyTo) {
				flyToRef.current = flyTo;
			}
		};

		const onSetRestrictToBounds = (e: Event) => {
			restrictToBoundsRef.current =
				(e as CustomEvent<boolean>).detail ?? true;
		};

		const onSetStyleSearch = (e: Event) => {
			const props = (e as CustomEvent<MuiTextFieldProps>).detail;
			setTextFieldProps(props ?? {});
		};

		container.addEventListener(SET_DEFAULT_SEARCH, onSetDefaultSearch);
		container.addEventListener(SEARCH_FLYTO, onSetFlyTo);
		container.addEventListener(RESTRICT_SEARCH, onSetRestrictToBounds);
		container.addEventListener(STYLE_SEARCH, onSetStyleSearch);

		return () => {
			container.removeEventListener(
				SET_DEFAULT_SEARCH,
				onSetDefaultSearch,
			);
			container.removeEventListener(SEARCH_FLYTO, onSetFlyTo);
			container.removeEventListener(
				RESTRICT_SEARCH,
				onSetRestrictToBounds,
			);
			container.removeEventListener(STYLE_SEARCH, onSetStyleSearch);
		};
	}, [geocoderId]);

	const shouldFlyToCenter = useCallback((): boolean => {
		const flyTo = flyToRef.current;
		if (flyTo === FlyTo.always) return true;
		if (!defaultSearchFiredRef.current && flyTo === FlyTo.onSelect)
			return true;
		if (defaultSearchFiredRef.current && flyTo === FlyTo.defaultSearch)
			return true;
		return false;
	}, []);

	const emitSearchChanged = useCallback(
		(result: GeocoderResult | null): void => {
			if (!geocoderId) return;
			const container = document.getElementById(geocoderId);
			if (!container) return;
			dispatchTargetedEvent(container, SEARCH_CHANGED, result);
		},
		[geocoderId],
	);

	const onInputChange = useCallback(
		async (query: string): Promise<GeocoderResult[]> => {
			const isCoord = query.match(COORD_REGEX);
			if (isCoord) {
				const lat = Number(isCoord[1]);
				const lng = Number(isCoord[2]);
				if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return [];
				return [
					{
						id: query,
						center: [lng, lat],
						geometry: {
							type: 'Point',
							coordinates: [lng, lat],
						},
						place_name: query,
						place_type: ['coordinate'],
						text: query,
						type: 'Feature',
					},
				];
			}

			const arcgisPromise = locator
				.addressToLocations(WORLD_GEOCODER_URL, {
					address: { SingleLine: query },
					maxLocations: 5,
					location: view?.center ?? undefined,
					...(restrictToBoundsRef.current && view?.extent
						? { searchExtent: view.extent }
						: {}),
				})
				.then(candidates =>
					candidates
						.filter(c => c.location != null && c.address != null)
						.slice(0, SEARCH_LIMIT)
						.map((c): GeocoderResult => {
							const address = c.address as string;
							const location = c.location as {
								longitude: number;
								latitude: number;
							};
							return {
								id: String(
									(
										c.attributes as Record<string, unknown>
									)?.['ResultID'] ?? address,
								),
								text: address,
								place_name: address,
								type: 'Feature',
								geometry: {
									type: 'Point',
									coordinates: [
										location.longitude,
										location.latitude,
									],
								},
								center: [location.longitude, location.latitude],
							};
						}),
				)
				.catch(() => [] as GeocoderResult[]);

			const endpointPromises = (searchEndpoints ?? []).map(
				({ url, token, dataServiceId }) =>
					new CustomSearchService(url, token, dataServiceId)
						.fetchData(query)
						.then(r => r.slice(0, SEARCH_LIMIT))
						.catch(() => [] as GeocoderResult[]),
			);

			const results = await Promise.all([
				arcgisPromise,
				...endpointPromises,
			]);
			return results.flat();
		},
		[view, searchEndpoints],
	);

	const onSelect = useCallback(
		(result: GeocoderResult | null): void => {
			if (!result) {
				setDefaultSearch('');
				setDropPin(null);
				emitSearchChanged(null);
				return;
			}

			const center = (result.center ??
				(result.geometry.type === 'Point' &&
					result.geometry.coordinates)) as [number, number];

			if (center && shouldFlyToCenter()) {
				if (view) {
					view.goTo({ center, zoom: 16 });
				} else {
					// Map still initializing — defer until view is ready
					pendingFlyToRef.current = center;
				}
			}

			setDropPin(center ?? null);

			if (result.serviceId && layerSelectionApi && getLayerIdsByServiceId) {
				const layerIds = getLayerIdsByServiceId(result.serviceId);
				layerSelectionApi.clearSelection();
				layerIds.forEach((layerId: string) => {
					layerSelectionApi.setSelectedItems(layerId, [result.id]);
				});
				if (layerIds.length && selectionApi) {
					selectionApi.selectAssets([result.id], false, layerIds[0]);
				}
			}

			defaultSearchFiredRef.current = false;
			emitSearchChanged(result);
			onSearchRef.current?.(result);
		},
		[
			view,
			layerSelectionApi,
			getLayerIdsByServiceId,
			selectionApi,
			shouldFlyToCenter,
			emitSearchChanged,
			setDropPin,
		],
	);

	return { onInputChange, onSelect, defaultSearch, setDefaultSearch, textFieldProps };
};
